import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { BRAND_ASSETS } from '../../core/constants/brand-assets';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;
  resetForm: FormGroup;
  loading = false;
  verifying = true;
  errorMessage = '';
  successMessage = '';
  showPassword = false;
  showConfirmPassword = false;
  resetCode = '';
  email = '';
  invalidCode = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  async ngOnInit(): Promise<void> {
    this.resetCode = this.route.snapshot.queryParams['oobCode'] || '';

    if (!this.resetCode) {
      this.errorMessage = 'Invalid or missing password reset code';
      this.invalidCode = true;
      this.verifying = false;
      return;
    }

    await this.verifyResetCode();
  }

  async verifyResetCode(): Promise<void> {
    this.verifying = true;
    this.errorMessage = '';

    try {
      this.email = await this.authService.verifyPasswordResetCode(this.resetCode);
      this.verifying = false;
    } catch (error: any) {
      console.error('Verify reset code error:', error);
      this.errorMessage = 'This password reset link has expired or is invalid. Please request a new password reset.';
      this.invalidCode = true;
      this.verifying = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { password } = this.resetForm.value;

      await this.authService.confirmPasswordReset(this.resetCode, password);
      // Same-origin sign-in: Firebase auth state lands in this portal's storage,
      // so the user goes straight to the admin dashboard.
      await this.authService.signIn(this.email, password);
      this.router.navigate(['/dashboard']);
    } catch (error: any) {
      console.error('Reset password error:', error);

      if (error.code === 'auth/expired-action-code') {
        this.errorMessage = 'This password reset link has expired. Please request a new password reset.';
      } else if (error.code === 'auth/invalid-action-code') {
        this.errorMessage = 'This password reset link is invalid or has already been used.';
      } else if (error.code === 'auth/weak-password') {
        this.errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else {
        this.errorMessage = error.message || 'Failed to reset password. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  goToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  goBackToLogin(): void {
    this.router.navigate(['/login']);
  }

  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumeric = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar)
      ? null
      : { weakPassword: true };
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');
    if (!password || !confirmPassword) return null;
    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  get passwordErrors(): string[] {
    const errors: string[] = [];
    const passwordControl = this.resetForm.get('password');

    if (passwordControl?.touched && passwordControl?.errors) {
      if (passwordControl.errors['required']) errors.push('Password is required');
      if (passwordControl.errors['minlength']) errors.push('Password must be at least 8 characters');
      if (passwordControl.errors['weakPassword']) errors.push('Password must contain uppercase, lowercase, number, and special character');
    }

    return errors;
  }
}
